import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Actions, Store } from '@ngxs/store';
import { FeatureService } from 'app/library/feature/services/feature.service';
import { ModuleKinds } from 'app/library/module/models/module-kinds';
import { Module } from 'app/library/module/models/module.model';
import { MaxBrainTag } from 'app/library/tag/models/tag.model';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { IAllModuleDialogListInterface } from '../../interfaces/all-modules-dialog-list.interface';
import { AllModuleDataSource } from '../list/datasource';

@Component({
    selector: 'app-module-dialog-list',
    templateUrl: './dialog-list.component.html',
    styleUrls: ['./dialog-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AllModuleDialogListComponent extends UnsubscribeOnDestroy implements OnInit {
    private static readonly BOOKING_CONTEXT = 'booking';
    @Input()
    dataSource: AllModuleDataSource;

    @Input()
    displayedColumns: string[];

    @Input()
    entitie$: Observable<Module[]>;

    @Input()
    totalCount$: Observable<number>;

    @Input()
    searchInput: FormControl;

    @Input()
    tag$: Observable<MaxBrainTag[]>;
    tagIds: string[] = [];

    @Input()
    selectedModules: Module[];

    @Output()
    addedToList = new EventEmitter<Module>();

    @Output()
    removedFromList = new EventEmitter<Module>();

    @Output()
    loadModules = new EventEmitter<IAllModuleDialogListInterface>();

    @Output()
    clickedTag = new EventEmitter<MaxBrainTag>();

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;

    moduleKinds = ModuleKinds;
    isCourseCatalogFeatureEnabled: boolean;

    constructor(private _store: Store, private _action$: Actions, private _featureService: FeatureService, public matDialog: MatDialog) {
        super();

        this.isCourseCatalogFeatureEnabled = this._featureService.checkFeatureStatus(FeatureSwitchName.CourseCatalog);
    }

    ngOnInit(): void {
        this.searchInput.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this._unsubscribeAll)).subscribe((searchTerm) => {
            this.loadModules.emit({
                resetPageIndex: true,
                search: (searchTerm || '').trim(),
                sort: this.sort,
                paginator: this.paginator,
                context: this.getModulesContext(),
            });
        });

        this.dataSource = new AllModuleDataSource(
            this.entitie$.pipe(
                map((modules) => {
                    return modules?.map((item) => {
                        if (this.selectedModules?.find((selectedModule) => selectedModule.id === item.id)) {
                            item = Object.assign(new Module(), item, {
                                selected: true,
                            });
                        }
                        return item;
                    });
                })
            ),
            this._store,
            this._action$
        );

        setTimeout(() => {
            this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
                this.loadModules.emit({
                    resetPageIndex: true,
                    search: this.searchInput.value,
                    sort: this.sort,
                    paginator: this.paginator,
                    context: this.getModulesContext(),
                });
            });

            this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                this.loadModules.emit({
                    resetPageIndex: true,
                    search: this.searchInput.value,
                    sort: this.sort,
                    paginator: this.paginator,
                    context: this.getModulesContext(),
                });
            });
        });

        this.tag$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((selectedTags, index) => {
                    this.tagIds = selectedTags ? selectedTags.map((tag) => tag.id) : [];
                    return index > 0;
                })
            )
            .subscribe(() => {
                this.loadModulesPage(true);
            });
    }

    visualLength(text: string): number {
        const ruler = document.getElementById('ruler');
        ruler.innerHTML = text;
        const toReturn = ruler.offsetWidth;
        ruler.innerHTML = '';
        return toReturn;
    }

    addToList(module: Module): void {
        this.addedToList.emit(module);
    }

    removeFromList(module: Module): void {
        this.removedFromList.emit(module);
    }

    isModuleSelectable(module: Module): boolean {
        if (!this.isCourseCatalogFeatureEnabled) {
            return true;
        }

        if (module?.selectionStatus) {
            return module.selectionStatus.isSelectable;
        }

        return !module?.listingId;
    }

    filterByTag(tag: MaxBrainTag): void {
        this.clickedTag.emit(tag);
    }

    loadModulesPage(resetPageIndex = false): void {
        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        this.dataSource.loadModules(
            { tags: this.tagIds },
            this.searchInput.value,
            this.sort.active,
            this.sort.direction,
            this.paginator.pageSize,
            this.paginator.pageIndex + 1,
            false,
            null,
            this.getModulesContext()
        );
    }

    private getModulesContext(): string {
        return this.isCourseCatalogFeatureEnabled ? AllModuleDialogListComponent.BOOKING_CONTEXT : null;
    }
}
